/**
 * NewReportPage - Create new risk/hazard reports
 * NOTE: Creating reports is NOT supported offline (v1)
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { REPORT_TYPES } from '../lib/constants';
import { blockIfOffline } from '../lib/offlineToast';
import { Card, TypeBadge, Spinner, Select, Input, Textarea, CustomButton as Button } from '../components/shared';

const NewReportPage = ({ setCurrentPage, currentRole }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    report_type: '',
    title: '',
    description: '',
    site_id: '',
    location_details: '',
    immediate_danger: false,
    anonymous: false,
    evidence_notes: ''
  });
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    loadSites();
    
    // Track online/offline status
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadSites = async () => {
    try {
      const res = await api.get('/sites');
      setSites(res.items);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const handleTypeSelect = (typeId) => {
    // Block if offline
    if (blockIfOffline('Create report')) return;
    
    setFormData(prev => ({ ...prev, report_type: typeId }));
    setStep(2);
  };

  const handleSubmit = async () => {
    // Block if offline
    if (blockIfOffline('Submit report')) return;
    
    setLoading(true);
    setError(null);
    try {
      await api.post('/risks', formData);
      setCurrentPage('risks');
    } catch (err) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page new-report-page" data-testid="new-report-page">
      <div className="page-header">
        <Button variant="ghost" onClick={() => setCurrentPage('risks')}>← Back to Reports</Button>
        <h1>New Report</h1>
        <p>Report a hazard, incident, or safety concern</p>
      </div>

      {/* Offline warning banner */}
      {isOffline && (
        <div className="offline-banner" data-testid="offline-banner">
          <span className="offline-icon">⚠</span>
          <span>You&apos;re offline. Creating reports is unavailable until you reconnect.</span>
        </div>
      )}

      {/* Progress Steps */}
      <div className="report-progress">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Type</span>
        </div>
        <div className="progress-line" />
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Details</span>
        </div>
        <div className="progress-line" />
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Review</span>
        </div>
      </div>

      {/* Step 1: Type Selection */}
      {step === 1 && (
        <div className="report-step" data-testid="step-type">
          <h2>What would you like to report?</h2>
          <div className="type-grid">
            {REPORT_TYPES.map(type => (
              <Card 
                key={type.id}
                className={`type-card ${formData.report_type === type.id ? 'selected' : ''}`}
                onClick={() => handleTypeSelect(type.id)}
                data-testid={`type-${type.id}`}
              >
                <span className="type-card-icon" style={{ backgroundColor: `${type.color}20` }}>
                  {type.icon ? <img src={type.icon} alt="" style={{ width: 24, height: 24 }} /> : type.emoji}
                </span>
                <h3 style={{ color: type.color }}>{type.label}</h3>
                <p>{type.description}</p>
                {(type.id === 'PSYCHOSOCIAL' || type.id === 'WELFARE') && (
                  <span className="restricted-tag">🔒 Restricted Access</span>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <div className="report-step" data-testid="step-details">
          <Card className="form-card">
            <h2>Report Details</h2>
            
            <Select
              label="Site *"
              value={formData.site_id}
              onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
              options={[
                { value: '', label: 'Select a site...' },
                ...sites.map(s => ({ value: s.id, label: s.name }))
              ]}
            />

            <Input
              label="Title *"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Brief description of the issue"
              maxLength={120}
            />

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Provide more details about what happened, where, and when..."
              rows={4}
            />

            <Input
              label="Specific Location"
              value={formData.location_details}
              onChange={(e) => setFormData(prev => ({ ...prev, location_details: e.target.value }))}
              placeholder="e.g., Level 2 stairwell, Loading Bay A"
            />

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.immediate_danger}
                  onChange={(e) => setFormData(prev => ({ ...prev, immediate_danger: e.target.checked }))}
                />
                <span className="checkbox-text danger">⚠️ Immediate danger - requires urgent attention</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.anonymous}
                  onChange={(e) => setFormData(prev => ({ ...prev, anonymous: e.target.checked }))}
                />
                <span className="checkbox-text">Submit anonymously</span>
              </label>
            </div>

            <Textarea
              label="Evidence Notes"
              value={formData.evidence_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, evidence_notes: e.target.value }))}
              placeholder="Describe any photos, documents, or other evidence..."
              rows={2}
            />

            <div className="form-actions">
              <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
              <Button 
                variant="primary" 
                onClick={() => setStep(3)}
                disabled={!formData.site_id || !formData.title}
              >
                Continue →
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="report-step" data-testid="step-review">
          <Card className="form-card">
            <h2>Review & Submit</h2>
            
            {error && <div className="error-banner">{error}</div>}

            <div className="review-section">
              <div className="review-row">
                <span className="review-label">Type</span>
                <TypeBadge type={formData.report_type} />
              </div>
              <div className="review-row">
                <span className="review-label">Site</span>
                <span>{sites.find(s => s.id === formData.site_id)?.name}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Title</span>
                <span>{formData.title}</span>
              </div>
              {formData.description && (
                <div className="review-row">
                  <span className="review-label">Description</span>
                  <span>{formData.description}</span>
                </div>
              )}
              {formData.location_details && (
                <div className="review-row">
                  <span className="review-label">Location</span>
                  <span>{formData.location_details}</span>
                </div>
              )}
              {formData.immediate_danger && (
                <div className="review-row">
                  <span className="review-label">Flags</span>
                  <span className="danger-flag">⚠️ Immediate Danger</span>
                </div>
              )}
              {formData.anonymous && (
                <div className="review-row">
                  <span className="review-label">Submission</span>
                  <span>Anonymous</span>
                </div>
              )}
            </div>

            <div className="form-actions">
              <Button variant="ghost" onClick={() => setStep(2)}>← Back</Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit}
                loading={loading}
                data-testid="submit-report-btn"
              >
                Submit Report
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NewReportPage;
